import React from 'react';
import { Stack, Box } from "@chakra-ui/react";
import Loader from './Loader';
import ZaraTable from 'components/custom-components/ZaraTable/ZaraTable';
import { TimeIcon, CalendarIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';

type CallTransfer = {
    id: string;
    beforTransferingMsg?: string;
    instructionBeforTransfer?: string;
    callerIdStatus?: string;
    warmTransferSettings?: {
        typeWarmTransfer?: string;
        onNoPickup?: string;
        templateMessage?: string;
    };
    [key: string]: any;
};

type CallTransfersResponse = {
    transfers: CallTransfer[];
    count: number;
    pagination: {
        page: number;
        perPage: number;
    };
    active?: boolean;
};

type TableHeader = {
    key: string;
    value: string;
    sortable: boolean;
};

type RowActions = {
    edit: (row: CallTransfer) => void;
    delete: (row: CallTransfer) => void;
};

type CallTransferListDisplayProps = {
    callTransfers: CallTransfersResponse | null;
    setEditRow: React.Dispatch<React.SetStateAction<CallTransfer | null>>;
    onDelete: (row: CallTransfer) => void;
    page: number;
    perPage: number;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
    warmTransferEnabled: boolean;
};

const callerIdStatusDict: Record<string, string> = {
    1: "Show caller's phone numbers",
    2: "Show AI agent's phone number"
};

const CallTransferListDisplay: React.FC<CallTransferListDisplayProps> = ({
    callTransfers,
    setEditRow,
    onDelete,
    page,
    perPage,
    onPageChange,
    onPerPageChange,
    warmTransferEnabled,
}) => {
    const tableHeaders: TableHeader[] = [
        { key: "phoneNumber", value: "Phone Number", sortable: false },
        { key: "beforTransferingMsg", value: "What do you want your agent to say before it transfers the call?", sortable: false },
        { key: "instructionBeforTransfer", value: "When do you want your agent to transfer calls to this number?", sortable: false },
        { key: "schedule", value: "Schedule", sortable: false }, // NEW
        { key: "callerIdStatus", value: "Caller Id", sortable: false },
        ...(warmTransferEnabled ? [{ key: "warmTransfer", value: "Warm Transfer", sortable: false }] : [])
    ];
    if (!callTransfers) return <Loader />;
    const getOriginalRow = (row: CallTransfer) => {
        const found = callTransfers.transfers.find((transfer) => transfer.id === row.id);
        return found ? found : row;
    };

    const rowActions: RowActions = {
        edit: (row: CallTransfer) => {
            setEditRow(getOriginalRow(row))
        },
        delete: (row: CallTransfer) => {
            onDelete(row);
        },
    };

    const onAddTableAction = () => {

        setEditRow({ new: "row", id: "", callerIdStatus: "1" } as CallTransfer);
    }

    const tableActions = {
        add: {
            action: onAddTableAction,
            title: "Add Call Transfer",
        },
    };

    function renderSchedule(preferredSchedule: any) {
        if (!preferredSchedule || !preferredSchedule.enabled) {
            // 24/7
            return (
                <Stack direction="row" align="center" spacing={2}>
                    <TimeIcon color="green.400" fontSize="lg" />
                    <Box as="span" px={3} py={1} borderRadius="lg" bg="green.50" color="green.600" fontWeight="bold" fontSize="sm">
                        24/7
                    </Box>
                </Stack>
            );
        }
        // If enabled, show time(s) and days
        const { schedule, start, end } = preferredSchedule;
        let timeRange = start && end ? `${start}-${end}` : schedule || '';
        const ranges = schedule && schedule.includes(',') ? (schedule.split(',').map((s: string) => s.trim())) : [timeRange];
        // Icon selection based on color (inspired by image)
        const iconForRange = (range: string) => {
            if (range.includes('6PM-10PM')) return <TimeIcon color="blue.400" fontSize="lg" />;
            if (range.includes('10AM-6PM')) return <CalendarIcon color="purple.400" fontSize="lg" />;
            if (range.includes('9AM-5PM')) return <TimeIcon color="blue.400" fontSize="lg" />;
            if (range.includes('8AM-12PM') || range.includes('2PM-6PM')) return <CalendarIcon color="orange.400" fontSize="lg" />;
            return <CalendarIcon color="blue.400" fontSize="lg" />;
        };
        return (
            <Stack direction="row" align="center" spacing={2}>
                {ranges.map((range: string, idx: number) => (
                    <Stack direction="row" align="center" spacing={1} key={idx}>
                        {iconForRange(range)}
                        <Box
                            as="span"
                            px={3}
                            py={1}
                            borderRadius="lg"
                            bg={
                                range.includes('6PM-10PM')
                                    ? 'blue.50'
                                    : range.includes('10AM-6PM')
                                        ? 'purple.50'
                                        : range.includes('8AM-12PM') || range.includes('2PM-6PM')
                                            ? 'orange.50'
                                            : range.includes('9AM-5PM')
                                                ? 'blue.50'
                                                : 'blue.50'
                            }
                            color={
                                range.includes('6PM-10PM')
                                    ? 'blue.700'
                                    : range.includes('10AM-6PM')
                                        ? 'purple.700'
                                        : range.includes('8AM-12PM') || range.includes('2PM-6PM')
                                            ? 'orange.700'
                                            : range.includes('9AM-5PM')
                                                ? 'blue.700'
                                                : 'blue.700'
                            }
                            fontWeight="bold"
                            fontSize="sm"
                        >
                            {range}
                        </Box>
                    </Stack>
                ))}
            </Stack>
        );
    }

    function renderWarmTransfer(warmTransferSettings: any) {
        return warmTransferSettings ? <CheckIcon color="green.500" /> : <CloseIcon color="red.500" />;
    }

    function modifyRows(rows: CallTransfer[]) {
        return rows.map(row => {
            const key = row.callerIdStatus !== undefined ? String(row.callerIdStatus) : "";
            return {
                ...row,
                callerIdStatus: callerIdStatusDict[key] || row.callerIdStatus,
                schedule: renderSchedule(row.preferredSchedule), // NEW
                ...(warmTransferEnabled ? { warmTransfer: renderWarmTransfer(row.warmTransferSettings) } : {}),
            };
        });
    }

    return (
        <ZaraTable
            headers={tableHeaders as any}
            rows={modifyRows(callTransfers.transfers)}
            pageDetails={{
                page,
                total: callTransfers.count,
                perpage: perPage,
            }}
            rowActions={rowActions as any}
            tableActions={tableActions}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
            noDataMessage='No call transfers found'
        />
    );
};

export default CallTransferListDisplay;